import { CommonTemplate } from "@components/templates/common";
import { useLogin, useTranslation, useLink } from "@refinedev/core";
import { CanAccess } from "@refinedev/core";
import { useState } from "react";
import { FaGoogle, FaUserDoctor, FaHeartPulse } from "react-icons/fa6";
import { PopupBlockedModal } from "@components/molecules/PopupBlockedModal";
import { motion } from "framer-motion";

export const Login = () => {
    const { translate } = useTranslation();
    const [isPopupBlocked, setIsPopupBlocked] = useState(false);

    const { mutate: login } = useLogin({
        mutationOptions: {
            onSettled: (data: any) => {
                if (data?.error?.message === 'common.auth.errors.popup-blocked') {
                    setIsPopupBlocked(true);
                }
            }
        }
    });

    const Link = useLink();

    const handleLogin = (formData?: any) => {
        const type = import.meta.env.DEV ? 'googlePopup' : 'google';
        login({ type, ...formData });
    };

    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const formData = Object.fromEntries(
            new FormData(e.currentTarget).entries(),
        );

        handleLogin(formData);

        e.currentTarget.reset();
    }

    return (
        <CommonTemplate>
            <div className="min-h-screen bg-base-200 flex flex-col lg:flex-row overflow-hidden">
                {/* LEFT PANEL: Branding & Visuals (Hidden on Mobile) */}
                <div 
                    className="hidden lg:flex lg:w-1/2 relative items-center justify-center p-12 overflow-hidden bg-cover bg-center"
                    style={{ backgroundImage: "url('/health_wellness_bg.png')" }}
                >
                    {/* Dark gradient overlay for readability and premium feel */}
                    <div className="absolute inset-0 bg-gradient-to-br from-base-300/80 via-base-300/60 to-primary/40 backdrop-blur-[2px]"></div>
                    
                    {/* Glowing decorative orbs (reduced opacity due to bg image) */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.5, ease: "easeOut" }}
                        className="absolute w-[800px] h-[800px] bg-secondary/10 rounded-full blur-[100px] -top-40 -left-40 mix-blend-screen"
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.5, ease: "easeOut", delay: 0.2 }}
                        className="absolute w-[600px] h-[600px] bg-primary/10 rounded-full blur-[80px] bottom-0 right-0 mix-blend-screen"
                    />

                    {/* Bento Grid layout */}
                    <div className="relative z-10 w-full max-w-lg grid grid-cols-2 gap-6">
                        {/* Main Logo Card */}
                        <motion.div
                            initial={{ y: 50, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="col-span-2 bg-base-100/60 backdrop-blur-xl border border-base-content/10 rounded-[2rem] p-10 shadow-2xl flex flex-col items-center justify-center min-h-[240px] hover:bg-base-100/80 transition-colors"
                        >
                            <img src="/hachimind.svg" alt="Hachi logo" className="w-72 drop-shadow-xl hover:scale-105 transition-transform duration-500" />
                        </motion.div>

                        {/* Feature Card 1 */}
                        <motion.div
                            initial={{ x: -50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="bg-primary text-primary-content rounded-[2rem] p-8 shadow-xl flex flex-col justify-between aspect-square hover:-translate-y-2 transition-transform duration-300"
                        >
                            <FaHeartPulse className="text-5xl opacity-90" />
                            <div>
                                <div className="text-3xl font-extrabold mb-2 leading-tight">Bienestar<br />Integral</div>
                                <div className="text-sm opacity-90 font-medium">Físico, mental y emocional para ti y tu entorno.</div>
                            </div>
                        </motion.div>

                        {/* Feature Card 2 */}
                        <motion.div
                            initial={{ x: 50, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            className="bg-secondary text-secondary-content rounded-[2rem] p-8 shadow-xl flex flex-col justify-between aspect-square hover:-translate-y-2 transition-transform duration-300"
                        >
                            <FaUserDoctor className="text-5xl opacity-90" />
                            <div>
                                <div className="text-3xl font-extrabold mb-2 leading-tight">Red de<br />Apoyo</div>
                                <div className="text-sm opacity-90 font-medium">Conecta con personal experto y adaptado a ti.</div>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* RIGHT PANEL: Login Form */}
                <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12 relative overflow-hidden min-h-screen lg:min-h-0 bg-base-200">
                    {/* Subtle mobile background glow */}
                    <div className="absolute lg:hidden w-[500px] h-[500px] bg-secondary/30 rounded-full blur-[80px] top-0 left-0 -translate-x-1/2 -translate-y-1/2" />
                    <div className="absolute lg:hidden w-[400px] h-[400px] bg-primary/20 rounded-full blur-[80px] bottom-0 right-0 translate-x-1/3 translate-y-1/3" />

                    <motion.div
                        initial={{ y: 30, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.7, ease: "easeOut" }}
                        className="w-full max-w-md relative z-10"
                    >
                        {/* Mobile Logo */}
                        <div className="lg:hidden flex justify-center mb-12">
                            <motion.img
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.2, duration: 0.5 }}
                                src="/hachimind.svg"
                                alt="Hachi logo"
                                className="w-56 drop-shadow-lg"
                            />
                        </div>

                        {/* Login Card */}
                        <div className="card bg-base-100 shadow-2xl rounded-[2.5rem] border border-base-content/5 backdrop-blur-xl">
                            <form className="card-body p-8 sm:p-12" onSubmit={(e) => onSubmit(e as any)}>
                                <div className="text-center mb-10">
                                    <CanAccess resource="onboarding" action="create" params={{ domain: 'pro_band0' }}>
                                        <h1 className="text-3xl font-bold text-base-content mb-3 flex flex-wrap justify-center gap-2 items-center">
                                            Hachimind <span className="text-primary">{translate("page.login.professional")}</span>
                                        </h1>
                                    </CanAccess>
                                    <CanAccess resource="onboarding" action="create" params={{ domain: 'user_band0' }}>
                                        <h1 className="text-3xl font-bold text-base-content mb-3 flex flex-wrap justify-center gap-2 items-center">
                                            Hachimind <span className="text-primary">{translate("page.login.users")}</span>
                                        </h1>
                                    </CanAccess>
                                    <p className="text-base-content/60 font-medium">
                                        {translate("page.login.title")}
                                    </p>
                                </div>

                                {/* Login Action */}
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="btn btn-outline hover:bg-base-content hover:text-base-100 hover:border-base-content btn-lg w-full rounded-2xl gap-3 shadow-sm border-2"
                                    type="submit"
                                >
                                    <FaGoogle className="text-2xl" />
                                    {translate("page.login.google")}
                                </motion.button>

                                <div className="divider my-8 text-base-content/30 text-xs uppercase tracking-widest font-semibold">OR</div>

                                {/* Registration Logic per tenant */}
                                <CanAccess resource="onboarding" action="create" params={{ domain: 'pro_band0' }}>
                                    <Link className="btn btn-primary btn-block btn-lg rounded-2xl shadow-lg shadow-primary/30" to="/onboarding">
                                        {translate("page.login.registry")}
                                    </Link>
                                </CanAccess>
                            </form>
                        </div>
                    </motion.div>
                </div>
            </div>

            <PopupBlockedModal
                isOpen={isPopupBlocked}
                onClose={() => setIsPopupBlocked(false)}
                onTryAgain={() => handleLogin()}
            />
        </CommonTemplate>
    );
}
